import Image from "next/image";
import { useState } from "react";
import Markdown from "react-markdown";
import { useGetDeviceInfo } from "utils/useGetDeviceInfo";

import CustomLink from "@/components/elements/custom-link";
import NextImage from "@/components/elements/image";
import BasicModal from "@/components/elements/modal";
import AppleStore from "@/components/icons/app-store";
import PlayStore from "@/components/icons/play store";

const columns = [
  {
    id: "1",
    title: "Products",
    links: [
      {
        id: "9",
        url: "/",
        text: "Onmo Card",
      },
    ],
  },
  {
    id: "2",
    title: "Help & Support",
    links: [
      {
        id: "24",
        url: "/help/customer-care",
        text: "Customer Care",
      },
      {
        id: "26",
        url: "/help/fraud-prevention",
        text: "Fraud Prevention",
      },
      {
        id: "21",
        url: "/help/faq",
        text: "FAQs",
      },
      {
        id: "23",
        url: "/help/fx",
        text: "FX Calculator",
      },
    ],
  },
  {
    id: "3",
    title: "Legal",
    links: [
      {
        id: "13",
        url: "/legal/terms-and-conditions",
        text: "Terms and Conditions",
      },
      {
        id: "12",
        url: "/legal/privacy-policy",
        text: "Privacy Policy",
      },
      {
        id: "14",
        url: "/legal/cookie-notice",
        text: "Cookie Notice",
      },
    ],
  },
  {
    id: "4",
    title: "Contact",
    links: [
      {
        id: "15",
        url: "/contact-us",
        text: "Contact Us",
      },
      {
        id: "16",
        url: "https://onmo.teamtailor.com/",
        text: "Careers",
        newTab: true,
      },
    ],
  },
];

const Footer = ({ footer, landing = false }) => {
  const date = new Date().getUTCFullYear();

  const [downloadModal, setDownloadModal] = useState(false);

  const { deviceType, redirectUrl } = useGetDeviceInfo();

  return (
    <>
      <footer
        id="landing-footer"
        className={`${
          landing ? "bg-onmoPurple-100" : "bg-onmoPrimaryBlack"
        } text-white font-euclid rounded-t-spacing-9 `}
      >
        <div className="mx-auto px-8 lg:px-10 pt-16 pb-8 lg:pt-[72px] lg:pb-[140px]">
          <nav className="lg:flex flex-wrap flex-col justify-between gap-10 mb-10">
            <div
              className={`row-span-1 flex items-center lg:justify-center pb-10 border-b ${
                landing ? "border-onmoPurple-300" : "border-onmoGrey-light"
              }`}
            >
              {footer?.logo && !landing && (
                <NextImage width="150" height="35" media={footer.logo.data} />
              )}

              {landing && (
                <Image
                  height={35}
                  width={150}
                  src="/images/onmo_logo_purple.svg"
                  alt="Onmo Logo"
                />
              )}
            </div>
            <div
              className={`row-span-1 border-b ${
                landing ? "border-onmoPurple-300" : "border-onmoGrey-light"
              }`}
            >
              <div className="lg:flex flex-row gap-spacing-7 justify-between pb-10 max-w-spacing-920 mx-auto">
                {columns.map(footerColumn => (
                  <div
                    key={footerColumn.id}
                    className="mt-10 lg:mt-0 lg:w-[212px]"
                  >
                    <p
                      className={`font-medium text-size-2  ${
                        landing ? "text-onmoText" : "text-onmoSecondary"
                      }`}
                    >
                      {footerColumn.title}
                    </p>
                    <ul className="mt-spacing-7 flex flex-col gap-spacing-7">
                      {footerColumn.links.map(link => (
                        <li
                          key={link.id}
                          className={`text-size-1 font-normal leading-4 ${
                            landing ? "text-onmoPrimary" : "text-onmoGrey-muted"
                          } `}
                        >
                          <CustomLink link={link}>{link.text}</CustomLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex lg:justify-around">
              {footer.appStoreIcons ? (
                <div className="flex flex-col gap-4 mt-10 lg:mt-0">
                  <p
                    className={`text-sm font-medium leading-4 lg:text-base cursor-pointer ${
                      landing ? "text-onmoPrimary" : "text-onmoGrey-faint"
                    } lg:text-center`}
                    onClick={e => {
                      e.preventDefault();
                      if (deviceType === "ANDROID" || deviceType === "IOS") {
                        window.open(redirectUrl, "_self");
                      } else {
                        setDownloadModal(true);
                      }
                    }}
                  >
                    Download the app
                  </p>
                  <div className="flex flex-row justify-between gap-3 lg:mt-0 ml-[-6px] md:ml-0 md:p-spacing-2 target:_blank">
                    <a
                      target="_blank"
                      aria-label="Apple app store"
                      href="https://apps.apple.com/gb/app/onmo/id6498719371"
                      rel="noreferrer"
                    >
                      <AppleStore />
                    </a>
                    <a
                      target="_blank"
                      aria-label="Google play store"
                      href="https://play.google.com/store/apps/details?id=app.onmo.v2&gl=UK"
                      rel="noreferrer"
                    >
                      <PlayStore />
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          </nav>
          <div
            className={`text-xs leading-4 pt-10 flex flex-col gap-3 border-t ${
              landing ? "border-onmoPurple-300" : "border-onmoGrey-light"
            } `}
          >
            <div className="max-w-spacing-920 mx-auto">
              <div className="flex flex-col gap-4 text-onmoGrey-medium text-sm">
                <p>
                  Onmo Ltd. is authorised and regulated by the Financial Conduct
                  Authority (FCA), ref. 940604. The company is registered in
                  England & Wales (Number 12708619) at 33 Cannon Street, 4th
                  Floor, London, England, EC4M 5SB.
                </p>
                <p>
                  Onmo Visa Card is issued by Transact Payments Limited.
                  Transact Payments is authorised and regulated by the Gibraltar
                  Financial Services Commission. Visa is a registered trademark
                  of Visa International Services Limited.
                </p>
              </div>
              <Markdown className="flex flex-col text-onmoGrey-medium mt-4">
                {footer.copyright.replace("{currentYear}", date)}
              </Markdown>
            </div>
          </div>
        </div>
      </footer>
      {downloadModal && (
        <BasicModal
          handleClose={() => {
            setDownloadModal(false);
          }}
          open={downloadModal}
        />
      )}
    </>
  );
};

export default Footer;
